import moment from "moment";

export function formatSeq(sequenceNumber) {
    if (sequenceNumber == null) return '';
    const asString = `${sequenceNumber}`;
    const parsed = /^\d{8}$/.test(asString)
        ? moment(asString, 'YYYYMMDD')
        : moment(sequenceNumber);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : asString;
}

export function clamp(value, min, max) {
    const n = Number(value);
    if (isNaN(n)) return min;
    return Math.min(max, Math.max(min, n));
}

export function fullItemIdentity(item, origin) {
    const base = item ?? {};
    return {
        ...base,
        id: base.id ?? base.inventoryItemId,
        inventoryItemId: base.inventoryItemId ?? base.id,
        origin: base.origin ?? origin ?? 'PROTOCOL'
    };
}

export function itemAssignment(componentAssignment, line) {
    const lineAssignment = line?.assignment ?? {};
    const kitNumber = lineAssignment.kitNumber ?? line?.kitNumber ?? null;
    return {
        assignmentType: componentAssignment?.assignmentType ?? 'Protocol',
        assignId: componentAssignment?.assignId,
        ...lineAssignment,
        ...(kitNumber != null ? { kitNumber } : {})
    };
}

export function normalizeMovementItem(moveItem, componentAssignment) {
    if (moveItem == null) return moveItem;
    moveItem.item = fullItemIdentity(moveItem.item, moveItem.item?.origin);
    moveItem.assignment = itemAssignment(componentAssignment, moveItem);
    return moveItem;
}

export function normalizeMovementItems(items, componentAssignment) {
    return (items ?? []).map(i => normalizeMovementItem(i, componentAssignment));
}

export function buildReturnCatalog(comprobantes, componentAssignment) {
    return comprobantes.flatMap(c =>
        c.items.map(line => ({
            originMoveItemId: line.originMoveItemId,
            originDispenseMovementId: c.originDispenseMovementId,
            sequenceNumber: c.sequenceNumber,
            item: fullItemIdentity(line.item, line.origin),
            release: line.release,
            assignment: itemAssignment(componentAssignment, line),
            presentation: line.presentation,
            perUnitTotal: line.perUnitTotal,
            dispensedKitQuantity: line.dispensedKitQuantity ?? null,
            unitCount: line.unitCount
        }))
    );
}

export function remainingByOrigin(catalog, entries, passThroughCounts) {
    const used = new Map();
    for (const e of entries) {
        used.set(e.originMoveItemId, (used.get(e.originMoveItemId) ?? 0) + 1);
    }
    const remaining = new Map();
    for (const line of catalog) {
        const taken = (used.get(line.originMoveItemId) ?? 0)
            + (passThroughCounts?.get(line.originMoveItemId) ?? 0);
        remaining.set(line.originMoveItemId, Math.max(0, line.unitCount - taken));
    }
    return remaining;
}

export function hydrateEntriesFromValue(catalog, returnMove, dispenseItems) {
    const byOrigin = new Map(catalog.map(l => [l.originMoveItemId, l]));
    const reDispensePool = new Map();
    for (const it of dispenseItems ?? []) {
        if (it?.originMoveItemId == null) continue;
        reDispensePool.set(it.originMoveItemId, (reDispensePool.get(it.originMoveItemId) ?? 0) + 1);
    }

    const entries = [];
    for (const it of returnMove?.items ?? []) {
        const line = it?.originMoveItemId != null ? byOrigin.get(it.originMoveItemId) : null;
        if (line == null) continue;
        const paired = (reDispensePool.get(line.originMoveItemId) ?? 0) > 0;
        if (paired) reDispensePool.set(line.originMoveItemId, reDispensePool.get(line.originMoveItemId) - 1);
        entries.push({
            ...line,
            returned: it.partialQuantity ?? it.quantity ?? 0,
            reDispense: paired,
            releaseTargetState: it.releaseTargetState ?? null,
            condition: it.releaseCondition ?? null
        });
    }
    return entries;
}

export function deriveReDispenseRows(entries) {
    return entries
        .filter(e => e.reDispense && e.returned > 0)
        .map(e => ({
            key: e.entryKey,
            entryKey: e.entryKey,
            item: e.item,
            release: e.release,
            assignment: e.assignment,
            presentation: e.presentation,
            originMoveItemId: e.originMoveItemId,
            partialQuantity: e.returned,
            dispensedKitQuantity: e.dispensedKitQuantity ?? null
        }));
}
